<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        View Webpages
      </span>
      <a-space>
        <a-button size="large" class="a-header--button slide-down-enter-active" type="default" v-on:click="downloadCsv" :loading="isDownloadingCsv" :disabled="isDownloadingCsv">
          <a-icon v-if="!isDownloadingCsv" type="download" theme="outlined" />
          {{ isDownloadingCsv ? 'Downloading...' : 'Download All URLs in CSV' }}
        </a-button>
        <a-button size="large" class="a-header--button slide-down-enter-active" type="primary">
          <router-link :to="'/webpages/add'">
            <a-icon type="plus-square" theme="filled" />
            Add New
          </router-link>
        </a-button>
      </a-space>
    </div>
    <a-tabs v-if="isArchiveFeatureEnabled" v-model="activeTab" :animated="false" class="webpage-tabs">
      <a-tab-pane key="active" tab="Active" />
      <a-tab-pane key="archived" tab="Archived" />
    </a-tabs>
    <ArchivedWebpageList v-if="currentTab === 'archived'" @restored="onRestoredFromArchive" />
    <div v-show="currentTab === 'active'" class="active-tab-content flex-grow-1 d-flex flex-column overflow-y-hidden">
      <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
        <Spinner />
      </div>
      <transition name="slide-up">
        <div v-if="!isLoading" class="a-filter">
          <a-row class="full-w" type="flex" justify="start" align="middle" :gutter="24">
            <a-col :span="6">
              <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
                <a-button size="large">
                  <a-icon type="search" />
                </a-button>
                <a-input class="custom-prefix" placeholder="Search" @input="onSearch" />
              </div>
            </a-col>
            <a-col>
              <a-checkbox v-model="filterParams.exactMatch" @change="onChangeExactMatch">
                Exact match
              </a-checkbox>
            </a-col>
            <a-col :span="8">
              <div class="full-w a-select-group" :class="{ active: hasCombinedFilterActive }">
                <a-button size="large">
                  <a-icon type="filter" />
                </a-button>
                <!--  User interaction > [combinedFilter dropdown] > onChangeCombinedFilter() > filter() -->
                <a-select
                  v-model="filterParams.combinedFilter"
                  mode="multiple"
                  :size="'large'"
                  placeholder="Filter by Status, Plan Type, Tags..."
                  :maxTagCount="2"
                  :maxTagTextLength="12"
                  @change="onChangeCombinedFilter"
                >
                  <a-select-opt-group label="Status">
                    <a-select-option v-for="status in statusFilterOptions" :key="'status:' + status" :value="'status:' + status">
                      {{ status }}
                    </a-select-option>
                  </a-select-opt-group>
                  <a-select-opt-group label="Plan Type">
                    <a-select-option v-for="planType in planTypeOptions" :key="'planType:' + planType.value" :value="'planType:' + planType.value">
                      {{ planType.label }}
                    </a-select-option>
                  </a-select-opt-group>
                  <a-select-opt-group v-if="isTagsFeatureEnabled" label="Tags">
                    <a-select-option v-for="tag in availableTags" :key="'tag:' + tag" :value="'tag:' + tag">
                      {{ tag }}
                    </a-select-option>
                  </a-select-opt-group>
                </a-select>
              </div>
            </a-col>
            <a-col v-if="isHttpStatusColumnVisible" :span="6">
              <div class="full-w a-input-group" :class="{ active: !!filterParams.httpStatusSearch }">
                <a-button size="large">
                  <a-icon type="search" />
                </a-button>
                <a-input class="custom-prefix" placeholder="HTTP status (e.g. 200,404)" @input="onSearchHttpStatus" />
              </div>
            </a-col>
          </a-row>
          <a-row class="full-w mt-2" type="flex" justify="start">
            <a-button size="default" :disabled="!isHasSelected" type="danger" class="danger-btn" @click="onDeletes()">
              <a-icon type="delete" /> Delete
            </a-button>
            <a-button size="default" :disabled="!isHasSelected || isLoadingSelectedRows" :loading="isLoadingSelectedRows" class="ml-2 warning-btn" @click="onMultipleReBaseline()">
              <img v-if="!isHasSelected" class="mr-2" src="@assets/icon/off.svg">
              <img v-if="isHasSelected" class="mr-2" src="@assets/icon/OffHover.svg">
              Rebaseline
            </a-button>
            <a-button size="default" :disabled="!isHasSelected || isLoadingSelectedRows" :loading="isLoadingSelectedRows" class="ml-2 edit-plan-type-btn" @click="onBulkEdit()">
              <a-icon type="edit" />
              Edit Plan Type
            </a-button>
            <a-button v-if="isArchiveFeatureEnabled" size="default" :disabled="!isHasSelected" class="ml-2 archive-btn" @click="onArchives()">
              <a-icon type="inbox" />
              Archive
            </a-button>
            <!-- Wrap button in span to prevent Tooltip from hijacking classes on disabled buttons -->
            <!-- https://github.com/ant-design/ant-design/issues/21404#issuecomment-586800984 -->
            <a-tooltip title="Detects external third-party scripts and suggests policy rules to reduce false positives." placement="top">
              <span class="ml-2">
                <a-button
                  size="default"
                  class="policy-btn"
                  :disabled="!isHasSelected || isSuggestingPolicy"
                  :loading="isSuggestingPolicy"
                  @click="onSuggestPolicy"
                >
                  <a-icon v-if="!isSuggestingPolicy" type="bulb" />
                  Suggest Policy
                </a-button>
              </span>
            </a-tooltip>

            <a-button class="ml-2" type="primary" size="default" @click="showBatchSelectUrlsModal">
              Batch Select URLs
            </a-button>
            <a-modal
              :closable="false"
              title="Webpage URLs"
              width="45vw"
              :visible="isShowBatchSelectUrlsModal"
              @cancel="closeBatchSelectUrlsModal"
              @ok="handleSubmitBatchSelectUrls"
            >
              <a-textarea
                :placeholder="`eg. http://www.banffcyber.com/index.html (for regular HTTP) \nhttps://www.banffcyber.com/index.html (for secure HTTP) \nhttp://www.banffcyber.com:8080/demo/demo.html (for pages running on a non-default port, e.g. 8080)`"
                :autosize="{ minRows: 12, maxRows: 14 }"
                :value="batchSelectUrls"
                @change="handleChangeBatchSelectUrls"
              />
            </a-modal>
          </a-row>
        </div>
      </transition>
      <div v-resize="elSizeChange" class="a-body overflow-y-hidden flex-grow-1">
        <transition name="slide-up">
          <template v-if="!isLoading">
            <PaginationTable
              :key="columnsKey"
              :clientPaging="false"
              :hasSelectRow="true"
              :pagination="pagin"
              :columns="columnsToRender"
              :dataSource="data"
              :tableSize="tableSize"
              @changeSelectedRow="onSelectChange"
            >
              <div slot="urls" slot-scope="{ record }">
                <router-link :to="'./' + record.id" class="url text-wrap">
                  {{ record.url_name }}
                </router-link>
              </div>
              <div slot="last_verify" slot-scope="{ record }">
                <p class="time-text text-truncate">
                  <time>
                    {{ record.last_verify | dateString }}
                  </time>
                </p>
                <p class="time-text text-truncate">
                  <time>
                    {{ record.last_verify | timeString }}
                  </time>
                </p>
              </div>
              <template slot="operation" slot-scope="{ record }">
                <a-dropdown placement="topLeft">
                  <a-icon class="operation" type="ellipsis" />
                  <a-menu slot="overlay" class="operation-menu">
                    <a-menu-item v-if="record.health_status !== `Baseline Incomplete`" @click="onEdit(record)">
                      <img class="mr-2" src="@assets/icon/operation.svg">
                      Configuration
                    </a-menu-item>
                    <a-menu-item v-if="record.health_status !== `Awaiting Verification` && record.health_status !== `Baseline Incomplete`">
                      <router-link :to="record.id + '/response-performance'">
                        <img class="mr-2" src="@assets/icon/report.svg">
                        Performance
                      </router-link>
                    </a-menu-item>
                    <a-menu-item v-if="isArchiveFeatureEnabled">
                      <div class="full-wh" @click="showArchiveConfirm(record)">
                        <a-icon type="inbox" class="mr-2" />
                        Archive
                      </div>
                    </a-menu-item>
                    <a-menu-item>
                      <div class="full-wh" @click="showDeleteConfirm(record)">
                        <img class="mr-2" src="@assets/icon/delete.svg">
                        Delete
                      </div>
                    </a-menu-item>
                  </a-menu>
                </a-dropdown>
              </template>
              <template slot="status" slot-scope="{ record }">
                <a-tag :color="STATUS_COLOR[record.health_status]">
                  {{ record.health_status }}
                </a-tag>
              </template>
              <template slot="http_status" slot-scope="{ record }">
                <a-tag :color="getHttpStatusColor(record && record.status_code_monitoring && record.status_code_monitoring.http_status_code)">
                  {{ (record && record.status_code_monitoring && record.status_code_monitoring.http_status_code) || '-' }}
                </a-tag>
              </template>
              <template slot="http_status_title" slot-scope="{ col }">
                <span>
                  {{ col.title }}
                  <a-tooltip placement="top">
                    <template slot="title">
                      <span>
                        This info refreshes daily. Refer to Status for more accurate results.
                      </span>
                    </template>
                    <a-icon type="info-circle" class="ml-1" />
                  </a-tooltip>
                </span>
              </template>
              <template slot="plan_type" slot-scope="{ record }">
                {{ record.plan_type | lowercase | capitalize }}
              </template>
              <template slot="tags" slot-scope="{ record }">
                <span v-if="Array.isArray(record.tags) && record.tags.length">
                  <a-tag v-for="tag in record.tags" :key="tag.tagID" class="mr-1 mb-1">
                    {{ tag.name }}
                  </a-tag>
                </span>
                <span v-else>
                  -
                </span>
              </template>
              <span slot="polling_interval" slot-scope="{ record }">
                {{ POLLING_INTERVAL[record.polling_interval] | timeText }}
              </span>
            </PaginationTable>
          </template>
        </transition>
      </div>
      <div class="a-footer">
        <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
      </div>
    </div>
    <a-modal
      v-model="isShowMultipleBaseline"
      style="top: 20px;"
      width="80vw"
      class="rebaseline-modal"
      title="Rebaseline Webpages"
      cancelText="Close"
      okText="Submit Batch Job"
      :cancelButtonProps="{ style: { float: 'right' } }"
      :okButtonProps="{
        style: {
          marginRight: '8px',
          opacity: isSuccessMultipleBaseline ? '0' : '1',
        },
      }"
      :afterClose="afterCloseReBaselineModal"
      :closable="false"
      :maskClosable="false"
      @ok="submitMultipleRebaseline"
    >
      <div v-if="!isSuccessMultipleBaseline">
        <PaginationTable
          :columns="MultipleReBaselineColumns"
          :dataSource="multipleReBaselineData"
        >
          <template slot="webpageUrl" slot-scope="{ record }">
            <a class="url" :href="record.url" target="_blank" rel="noopener noreferrer">
              {{ record.url }}
            </a>
          </template>
          <template slot="healthStatus" slot-scope="{ record }">
            <a-tag :color="STATUS_COLOR[record.health_status]">
              {{ record.health_status }}
            </a-tag>
          </template>
          <template slot="lastVerify" slot-scope="{ record }">
            <p class="time-text">
              <time>
                {{ record.last_verify | dateString }}
              </time>
            </p>
            <p class="time-text">
              <time>
                {{ record.last_verify | timeString }}
              </time>
            </p>
          </template>
          <template slot="planType" slot-scope="{ record }">
            <p class="mb-0">
              {{ record.plan_type | lowercase | capitalize }}
            </p>
          </template>
        </PaginationTable>
      </div>
      <div v-if="isSuccessMultipleBaseline">
        <div class="success-rebaseline-container">
          <div>
            <a-icon type="check-circle" theme="filled" class="check-success" />
          </div>
          <div class="ml-3">
            <p class="mb-1 notice-text">
              {{ multipleReBaselineData.length }} webpages has been queued for rebaseline.
            </p>
            <p class="mb-1 notice-text">
              This may take a few minutes to a few hours depending on the number of pages.
            </p>
            <p class="mb-1 notice-text">
              You may check the status of this batch job
              <span>
                <router-link
                  class="nav-link-detail"
                  :to="{
                    path: '/batch-jobs/detail/RB' + batchIdsuccessMultipleBaseResponse,
                  }"
                >
                  ID
                  {{ 'RB' + batchIdsuccessMultipleBaseResponse }} at this link.
                </router-link>
              </span>
            </p>
            <p class="mb-0 notice-text">
              An email will be sent to {{ parentUserName }} when the job is finished.
            </p>
          </div>
        </div>
      </div>
    </a-modal>
    <a-modal
      v-model="bulkEdit.isVisible"
      style="top: 20px;"
      width="80vw"
      class="bulk-edit-modal"
      title="Bulk Edit Webpages Plan and Polling Interval"
      cancelText="Close"
      okText="Apply Changes"
      :cancelButtonProps="{ style: { float: 'right' } }"
      :okButtonProps="{
        props: {
          disabled: !bulkEdit.form.planType && !bulkEdit.form.pollingInterval,
          loading: bulkEdit.isSubmitting
        },
        style: {
          marginRight: '8px',
          opacity: bulkEdit.isSuccess ? '0' : '1',
        },
      }"
      :afterClose="afterCloseBulkEditModal"
      :closable="false"
      :maskClosable="false"
      @ok="submitBulkEdit"
    >
      <div v-if="!bulkEdit.isSuccess">
        <a-row :gutter="24" class="mb-4">
          <a-col :span="8">
            <a-form-item label="Plan Type" class="mb-0">
              <a-select
                v-model="bulkEdit.form.planType"
                size="large"
                @change="onBulkEditPlanTypeChange"
              >
                <a-select-option v-for="planType in planTypeOptions" :key="planType.value" :value="planType.value">
                  {{ planType.label }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="Polling Interval" class="mb-0">
              <a-select
                v-model="bulkEdit.form.pollingInterval"
                size="large"
                :disabled="!bulkEdit.form.planType"
              >
                <a-select-option v-for="(label, value) in bulkEdit.pollingIntervalOptions" :key="+value" :value="+value">
                  {{ label | timeText }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
        <p class="text-muted mb-2">
          <a-icon type="info-circle" class="mr-1" />
          Polling interval options depend on the selected plan type.
        </p>
        <PaginationTable
          :columns="BulkEditColumns"
          :dataSource="bulkEdit.data"
          :pagination="bulkEdit.pagination"
        >
          <template slot="webpageUrl" slot-scope="{ record }">
            <a class="url" :href="record.url" target="_blank" rel="noopener noreferrer">
              {{ record.url }}
            </a>
          </template>
          <template slot="planType" slot-scope="{ record }">
            <p class="mb-0">
              {{ record.plan_type | lowercase | capitalize }}
            </p>
          </template>
          <template slot="pollingInterval" slot-scope="{ record }">
            {{ POLLING_INTERVAL[record.polling_interval] | timeText }}
          </template>
        </PaginationTable>
        <PaginationBar 
          :pageSize.sync="bulkEdit.pagination.pageSize" 
          :total="bulkEdit.pagination.total" 
          storageKey="bulkEditPageSize"
          @change="onBulkEditPaginChange" 
        />
      </div>
      <div v-if="bulkEdit.isSuccess && bulkEdit.result">
        <div class="success-bulk-edit-container" :class="{ 'has-errors': bulkEdit.result.summary && bulkEdit.result.summary.failed > 0 }">
          <div>
            <a-icon 
              :type="bulkEdit.result.summary && bulkEdit.result.summary.failed > 0 ? 'exclamation-circle' : 'check-circle'" 
              theme="filled" 
              :class="bulkEdit.result.summary && bulkEdit.result.summary.failed > 0 ? 'check-warning' : 'check-success'" 
            />
          </div>
          <div class="ml-3">
            <p class="mb-1 notice-text">
              {{ bulkEdit.result.message }}
            </p>
            <p v-if="bulkEdit.result.summary && bulkEdit.result.summary.updated > 0" class="mb-0 notice-text">
              The changes have been applied to {{ bulkEdit.result.summary.updated }} webpage(s).
            </p>
          </div>
        </div>
        
        <!-- Error details section -->
        <div v-if="bulkEdit.result.errors && bulkEdit.result.errors.length > 0" class="bulk-edit-errors mt-4">
          <p class="error-title mb-2">
            Failed to update the following URLs:
          </p>
          <div class="error-list">
            <div v-for="(err, index) in bulkEdit.result.errors" :key="index" class="error-item">
              <div class="error-url">
                {{ err.url }}
              </div>
              <div class="error-message">
                <template v-if="err.validation_errors">
                  <span v-for="(messages, field) in err.validation_errors" :key="field">
                    {{ messages.join(', ') }}
                  </span>
                </template>
                <template v-else>
                  {{ err.error }}
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
    <SuggestedPolicyModal
      :visible="showSuggestedPolicyModal"
      :groups="suggestedPolicyGroups"
      :allPolicies="allPoliciesForSuggestion"
      @close="showSuggestedPolicyModal = false"
    />
    <a-modal
      v-model="archiveModal.visible"
      title="Archive Webpages"
      okText="Archive"
      cancelText="Cancel"
      :okButtonProps="{ props: { loading: archiveModal.isSubmitting } }"
      @ok="submitArchive"
    >
      <p>
        You are about to archive {{ archiveModal.urlIds.length }} webpage(s).
        Monitoring will stop and the plan slot(s) will be freed.
        Alerts, policies and history are kept online for audit purposes for 3 months.
        You can restore an archived webpage from the Archived tab at any time within
        this period.
      </p>
      <a-form-item label="Remark (optional)" class="mb-0">
        <a-textarea
          v-model="archiveModal.remark"
          placeholder="e.g. Page decommissioned after site revamp"
          :maxLength="255"
          :autosize="{ minRows: 2, maxRows: 4 }"
        />
      </a-form-item>
    </a-modal>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import { RANGE } from '@constants/range';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import { STATUS_COLOR } from '@constants/statusColor';
  import { getHttpStatusColor } from '@shared/helpers/http-status';
  import { POLLING_INTERVAL } from '@constants/pollingInterval';
  import { WebpageColumns } from '../helper.js';
  import { MultipleReBaselineColumns } from '../columnsTable/MultipleReBaselineColumns';
  import { BulkEditColumns } from '../columnsTable/BulkEditColumns';
  import WebpageService from '../services/webpage.service';
  import AccountService from '../../Account/services/account.service';
  import SuggestedPolicyModal from '@shared/components/SuggestedPolicy/SuggestedPolicyModal.vue';
  import suggestedPolicyMixin from '@shared/mixins/suggestedPolicy.mixin.js';
  import webpageListMixin from '../mixins/webpageList.mixin';
  import ArchivedWebpageList from './ArchivedWebpageList.vue';

  const ALL_STATUS = 'All Status';
  // 'Archived' is not a filterable status here — archived webpages live in the Archived tab only
  const statusWebpages = Object.keys(STATUS_COLOR).filter((status) => status !== 'Archived');
  const statusList = [ALL_STATUS, ...statusWebpages];

  // ---> Constants > [statusFilterOptions] > Combined filter dropdown status group
  const statusFilterOptions = statusWebpages;

  export default {
    name: 'WebpageList',
    mixins: [suggestedPolicyMixin, webpageListMixin],
    components: {
      SuggestedPolicyModal,
      ArchivedWebpageList,
    },
    data() {
      let pageSize = Number(window.localStorage.getItem("pageSize"));
      if(!pageSize) {
        pageSize = 5;
      }

      return {
        RANGE,
        ALL_STATUS,
        DATE_FORMAT,
        STATUS_COLOR,
        POLLING_INTERVAL,
        statusList,
        statusFilterOptions,
        MultipleReBaselineColumns,
        BulkEditColumns,
        data: [],
        WebpageColumns,
        filterParams: {
          search: '',
          exactMatch: false,
          status: ALL_STATUS,
          httpStatusSearch: '',
          tags: [],
          combinedFilter: []  // Array of 'status:X', 'planType:X', 'tag:X' values
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0,
        },
        selectedRowKeys: [],
        visibleModal: false,
        isHandledChartData: false,
        chartData: {},
        timeUnit: 'hour',
        timeUnitStepSize: 2,
        performanceData: [],
        performanceWebpageName: '',
        performanceWebpageUrl: '',
        filterChartOptions: {},
        chartWidth: 'auto',
        isShowMultipleBaseline: false,
        multipleReBaselineData: [],
        isSuccessMultipleBaseline: false,
        batchIdsuccessMultipleBaseResponse: '',
        currentUserName: '',
        parentUserName: '',
        tableScrollHeight: 500,
        isLoading: true,
        isDownloadingCsv: false,
        tableSize: {
          height: '',
          width: '',
        },
        isDeleting: false,
        deletePopupStatus: {
          count: 0,
          total: 0
        },
        isShowBatchSelectUrlsModal: false,
        batchSelectUrls: '',
        // ?tab=archived deep-links straight to the Archived tab (e.g. back from the alerts audit view)
        activeTab: this.$route.query.tab === 'archived' ? 'archived' : 'active',
        archiveModal: {
          visible: false,
          remark: '',
          urlIds: [],
          isSubmitting: false,
        },
        isLoadingSelectedRows: false,
        allUrlRecords: null,
        bulkEdit: {
          isVisible: false,
          data: [],
          form: { planType: null, pollingInterval: null },
          pollingIntervalOptions: {},
          isSubmitting: false,
          isSuccess: false,
          result: null,
          pagination: { current: 1, pageSize: 5, total: 0 }
        },
      };
    },
    computed: {
      // ---> API /urls response + featureAccess > [isHttpStatusColumnVisible] > columnsToRender
      isHttpStatusColumnVisible() {
        const hasInResponse = Array.isArray(this.data) && this.data.some(function(item) {
          return item && item.status_code_monitoring;
        });
        return !!this.isStatusCodeMonitoringFeatureEnabled || hasInResponse;
      },
      // ---> featureAccess['status-code-monitoring'] > [isStatusCodeMonitoringFeatureEnabled] > isHttpStatusColumnVisible
      isStatusCodeMonitoringFeatureEnabled() {
        return this.$store.getters.hasFeature('status-code-monitoring');
      },
      // ---> featureAccess['url-archive'] > [isArchiveFeatureEnabled] > archive actions & Archived tab
      isArchiveFeatureEnabled() {
        return Boolean(this.featureAccess && this.featureAccess['url-archive']);
      },
      // ---> activeTab + isArchiveFeatureEnabled > [currentTab] > gate Archived view/tab
      // Falls back to 'active' when the feature is off so a ?tab=archived deep-link
      // can't strand a user on the archived view with no tab bar to get back.
      currentTab() {
        return this.isArchiveFeatureEnabled ? this.activeTab : 'active';
      },
      // ---> WebpageColumns + isHttpStatusColumnVisible > [columnsToRender] > PaginationTable.columns
      columnsToRender() {
        var cols = Array.isArray(this.WebpageColumns) ? this.WebpageColumns.slice() : [];
        cols = cols.filter(col => col.dataIndex !== 'tags' || this.isTagsFeatureEnabled);
        if (!this.isHttpStatusColumnVisible) {
          return cols;
        }
        // Build HTTP Status column definition
        var httpStatusCol = {
          title: 'HTTP Status',
          dataIndex: 'http_status_code',
          width: 120,
          slot: 'http_status',
          slotTitle: 'http_status_title',
          sorter: function(a, b) {
            var aCode = (a && a.status_code_monitoring && a.status_code_monitoring.http_status_code) || 0;
            var bCode = (b && b.status_code_monitoring && b.status_code_monitoring.http_status_code) || 0;
            return aCode - bCode;
          }
        };
        // Insert after the Status column (dataIndex: 'health_status')
        var statusIdx = cols.findIndex(function(c) { return c && c.dataIndex === 'health_status'; });
        if (statusIdx !== -1) {
          cols.splice(statusIdx + 1, 0, httpStatusCol);
        } else {
          cols.push(httpStatusCol);
        }
        return cols;
      },
      // ---> columnsToRender > [columnsKey] > force remount PaginationTable when columns set changes
      columnsKey() {
        var cols = this.columnsToRender || [];
        return cols.map(function(c){ return (c && c.title) || ''; }).join('|');
      }
    },
    // ---> route /webpages/list > [mounted] > getList() & getParentUser()
    async mounted() {
      // Load enums once for planTypeOptions and bulk edit polling intervals
      await this.loadEnums();
      this.getList();
      this.getParentUser();
      this.currentUserName = (this.$store.state && this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.username) || '';
    },
    methods: {
      // ---> HTTP Status code > [getHttpStatusColor] > http_status slot color
      getHttpStatusColor,
      async showBatchSelectUrlsModal() {
        try {
          const selectedRecords = await this._getSelectedRecords();
          this.batchSelectUrls = selectedRecords.map((item) => item.url).join('\n');
          this.isShowBatchSelectUrlsModal = true;
        } catch (err) {
          this.$showErrorMessage('Failed to load URLs. Please try again.');
        }
      },
      closeBatchSelectUrlsModal() {
        this.isShowBatchSelectUrlsModal = false;
      },
      handleChangeBatchSelectUrls(e) {
        this.batchSelectUrls = e.target.value;
      },
      /**
       * Fetches all account URLs via /urls/simple and caches the result.
       * Subsequent calls return the cached list until invalidated.
       */
      async fetchAllUrlRecords() {
        if (!this.allUrlRecords) {
          const allItems = await WebpageService.getSimpleURLList();
          this.allUrlRecords = Array.isArray(allItems) ? allItems : [];
        }
        return this.allUrlRecords;
      },
      async _getSelectedRecords() {
        const allRecords = await this.fetchAllUrlRecords();
        return allRecords.filter((item) => this.selectedRowKeysSet.has(item.id));
      },
      async handleSubmitBatchSelectUrls() {
        try {
          const allRecords = await this.fetchAllUrlRecords();
          const urlLookup = {};
          allRecords.forEach((item) => {
            if (item && item.url != null) {
              const key = String(item.url).trim();
              if (key && urlLookup[key] === undefined) {
                urlLookup[key] = item.id;
              }
            }
          });
          const selectedUrls = this.batchSelectUrls.split('\n').filter((url) => url.trim().length > 0);
          const selectedUrlIds = [];
          const notFound = [];
          selectedUrls.forEach((url) => {
            const trimmed = url.trim();
            const id = urlLookup[trimmed];
            if (id != null) {
              selectedUrlIds.push(id);
            } else {
              notFound.push(trimmed);
            }
          });
          if (notFound.length > 0) {
            throw new Error(`URL(s) do not exist: ${notFound.join(', ')}`);
          }

          // Sync isChecked for rows on the current page
          const selectedIdSet = new Set(selectedUrlIds);
          this.data.forEach((item) => {
            item.isChecked = selectedIdSet.has(item.id);
          });

          this.onSelectChange(selectedUrlIds, { replaceAll: true });
          this.$showSuccessMessage("Batch selected URLs successfully");
          this.closeBatchSelectUrlsModal();
        } catch (err) {
          this.$showErrorMessage(err.message);
        }
      },
      similarCompare(fisrtString, secondString) {
        const lengthFirstString = fisrtString.length;
        const lengtgSecondString = secondString.length;
        const maxLength = Math.max(lengthFirstString, lengtgSecondString);
        const minLength = Math.min(lengthFirstString, lengtgSecondString);
        let percentSimilar = 0;
        for (let iterable = 0; iterable < minLength; iterable++) {
          if (fisrtString[iterable].toLowerCase() === secondString[iterable].toLowerCase()) {
            percentSimilar++;
          }
        }
        return (percentSimilar / maxLength) * 100;
      },
      async onMultipleReBaseline() {
        if (this.selectedRowKeys.length === 0) return;
        this.isLoadingSelectedRows = true;
        try {
          this.multipleReBaselineData = await this._getSelectedRecords();
          this.isShowMultipleBaseline = true;
        } catch (err) {
          console.error('Failed to load selected URLs for rebaseline:', err);
          this.$showErrorMessage('Failed to load selected URLs. Please try again.');
        } finally {
          this.isLoadingSelectedRows = false;
        }
      },
      async getPagesForSuggestedPolicy() {
        return this._getSelectedRecords();
      },
      getSuggestedPolicyEmptyMessage() {
        return 'No selected webpages found.';
      },
      async onBulkEdit() {
        if (this.selectedRowKeys.length === 0) return;
        this.isLoadingSelectedRows = true;
        try {
          this.bulkEdit.data = await this._getSelectedRecords();
          this.bulkEdit.form = { planType: null, pollingInterval: null };
          this.bulkEdit.pollingIntervalOptions = {};
          this.bulkEdit.isSuccess = false;
          this.bulkEdit.isSubmitting = false;
          const savedPageSize = Number(window.localStorage.getItem('bulkEditPageSize')) || 5;
          this.bulkEdit.pagination = { current: 1, pageSize: savedPageSize, total: this.bulkEdit.data.length };
          this.bulkEdit.isVisible = true;
        } catch (err) {
          console.error('Failed to load selected URLs for bulk edit:', err);
          this.$showErrorMessage('Failed to load selected URLs. Please try again.');
        } finally {
          this.isLoadingSelectedRows = false;
        }
      },
      onBulkEditPaginChange(pagin) {
        this.bulkEdit.pagination = { ...pagin, total: this.bulkEdit.data.length };
      },
      onBulkEditPlanTypeChange(planType) {
        this.bulkEdit.pollingIntervalOptions = this.applyPollingOptionsForPlan(this.bulkEdit.form, planType);
      },
      async submitBulkEdit() {
        const { planType, pollingInterval } = this.bulkEdit.form;
        
        // Validate that at least one field is set
        if (!planType && !pollingInterval) {
          this.$showErrorMessage('Please select at least one field to update');
          return;
        }
        
        this.bulkEdit.isSubmitting = true;
        
        try {
          const urls = this.bulkEdit.data.map(item => item.url);
          const updatePayload = {
            plan_type: planType,
            polling_interval: pollingInterval,
          };
          const result = await WebpageService.updateUrlConfigBulk(urls, updatePayload);
          
          // Store the result for display
          this.bulkEdit.result = result;
          this.bulkEdit.isSuccess = true;
          this.selectedRowKeys = [];
          this.allUrlRecords = null;
          
          // Refresh the list to show updated data
          await this.getList();
        } catch (error) {
          this.$showErrorMessage('Failed to update webpages. Please try again.');
        } finally {
          this.bulkEdit.isSubmitting = false;
        }
      },
      afterCloseBulkEditModal() {
        this.bulkEdit.isSuccess = false;
        this.bulkEdit.form = { planType: null, pollingInterval: null };
        this.bulkEdit.pollingIntervalOptions = {};
        this.bulkEdit.result = null;
        this.bulkEdit.pagination.current = 1;
        this.bulkEdit.pagination.total = 0;
      },
      async onDeletes() {
        const selectedCount = this.selectedRowKeys.length;
        if (selectedCount === 0) return;
        this.$confirm({
          title: 'Are you sure to delete the ' + selectedCount + ' webpage(s)?',
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: async () => {
            this.deletePopupStatus.count = 0;
            this.deletePopupStatus.total = selectedCount;
            for (const id of this.selectedRowKeys) {
              await WebpageService.delete(id);
            }
            this.$showSuccessMessage('Webpages deleted successfully!');
            this.selectedRowKeys = [];
            this.allUrlRecords = null;
            return this.getList();
          },
        })
      },
      onDelete(record) {
        WebpageService.delete(record.id).then(() => {
          this.$showSuccessMessage('Deleted webpage');
          this.allUrlRecords = null;
          this.getList();
        });
      },
      onEdit(record) {
        this.$router.push({
          path: '/webpages/' + record.id,
        });
      },
      onSearch: debounce(function (event) {
        this.runSearch(event);
      }, 800),
      onSearchHttpStatus: debounce(function (event) {
        const raw = (event.target.value && String(event.target.value)) || '';
        this.filterParams.httpStatusSearch = raw;
        this.pagin.current = 1; // Reset to first page when filter changes
        this.getList();
      }, 800),
      onCancel() {
        this.$router.push('/webpages/list');
      },
      onChangeStatus(value) {
        this.filterParams.status = value;
        this.pagin.current = 1;
        this.getList();
      },
      onChangeTags(value) {
        this.filterParams.tags = Array.isArray(value) ? value : [];
        this.pagin.current = 1;
        this.getList();
      },
      showDeleteConfirm(record) {
        this.$confirm({
          title: 'Are you sure delete this item?',
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: () => {
            this.onDelete(record);
          },
        });
      },
      showArchiveConfirm(record) {
        this.openArchiveModal([record.id]);
      },
      onArchives() {
        if (this.selectedRowKeys.length === 0) return;
        this.openArchiveModal([...this.selectedRowKeys]);
      },
      openArchiveModal(urlIds) {
        this.archiveModal = {
          visible: true,
          remark: '',
          urlIds,
          isSubmitting: false,
        };
      },
      async submitArchive() {
        this.archiveModal.isSubmitting = true;
        try {
          const result = await WebpageService.archive(this.archiveModal.urlIds, this.archiveModal.remark || null);
          this.archiveModal.visible = false;

          if (result && result.summary && result.summary.failed > 0) {
            this.$showWarningMessage(result.message);
          } else {
            this.$showSuccessMessage((result && result.message) || 'Webpage(s) archived successfully');
          }

          // Drop the archived rows from the current selection and refresh
          const archivedIdSet = new Set(this.archiveModal.urlIds);
          this.selectedRowKeys = this.selectedRowKeys.filter((key) => !archivedIdSet.has(key));
          this.allUrlRecords = null;
          this.getList();
        } catch (error) {
          this.$showErrorMessage('Failed to archive webpage(s). Please try again.');
        } finally {
          this.archiveModal.isSubmitting = false;
        }
      },
      // ---> ArchivedWebpageList restore success > [onRestoredFromArchive] > refresh active list
      onRestoredFromArchive() {
        this.allUrlRecords = null;
        this.getList();
      },
      async getList() {
        try {
          // Build query options from current filter state
          const { statuses, planTypes, tags } = this.parsedCombinedFilter;
          const tagIds = this.getTagIdsByNames(tags);
          const options = {
            page: this.pagin.current,
            itemsPerPage: this.pagin.pageSize,
            q: this.filterParams.search || '',
            exact: this.filterParams.exactMatch,
            statuses: statuses,
            planTypes: planTypes,
            tagIds: tagIds,
            statusCode: this.filterParams.httpStatusSearch ? parseInt(this.filterParams.httpStatusSearch.split(/[,\s]/)[0], 10) || null : null
          };

          const [response, tagsData] = await Promise.all([
            WebpageService.getList(options),
            this.loadTagsIfNeeded()
          ]);

          this.applyTagsData(tagsData);

          // Handle paginated response
          const items = response.data || response || [];
          this.data = this.mapListRows(items);

          // Update pagination from server response
          if (response.meta) {
            this.pagin.total = response.meta.total;
          } else {
            this.pagin.total = items.length;
          }
        } catch (error) {
          console.error('Error fetching webpages:', error);
          this.$showErrorMessage('HTTP error received from web server.!');
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      handleOk() {
        this.visibleModal = false;
      },
      setRowKeyMultipleReBaseline(record, index) {
        return record.id || index;
      },
      async submitMultipleRebaseline() {
        const urlIds = this.multipleReBaselineData.map(item => item.id);
        try {
          const successResponse = await WebpageService.reBaselineMultipleWebpages(urlIds);
          this.batchIdsuccessMultipleBaseResponse = successResponse.id;
          this.isSuccessMultipleBaseline = true;
          this.selectedRowKeys = [];
          this.allUrlRecords = null;
        } catch {
          this.isShowMultipleBaseline = false;
        }
      },
      afterCloseReBaselineModal() {
        this.isSuccessMultipleBaseline = false;
      },
      elSizeChange(size) {
        this.tableSize = size;
      },
      async getParentUser() {
        const data = await AccountService.getUser();
        this.parentUserName = data.name;
        return data;
      },
      async downloadCsv() {
        this.isDownloadingCsv = true;
        try {
          // Fetch all pages automatically in batch size of 1000 via /urls/simple (no relationships loaded)
          const allData = await WebpageService.getSimpleURLList();

          // Build CSV
          let link = document.createElement('a');
          let csvData = [];
          let csvHeader = ['No.', 'Webpage Name', 'Webpage URL', 'Plan Type', 'Polling Interval', 'Last Verified (UTC)', 'Status'];
          csvData.push(csvHeader);
          for (let i = 0; i < allData.length; i++) {
            const entry = allData[i];
            csvData.push([i + 1, entry.config_name, entry.url_name, entry.plan_type, entry.polling_interval, entry.last_verify, entry.health_status]);
          }

          // Format data into string
          let csvDataStringRows = [];
          for (let row of csvData) {
            row = row.map(val => {
              if (val == null) {
                return '""';
              } else {
                let valReplacedSpecialChars = val.toString().replace(/[\r\n\t]/gmi, "").replace('"', '""');
                return '"' + valReplacedSpecialChars + '"';
              }
            });
            csvDataStringRows.push(row.join(","));
          }
          let csvDataString = csvDataStringRows.join("\n");

          link.href = window.URL.createObjectURL(new Blob([csvDataString], {type: 'text/csv;charset=utf-8;'}));
          link.setAttribute('download', "webpages.csv");
          document.body.appendChild(link);
          link.click();
          window.setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
          }, 1000);
        } catch (error) {
          console.error('Error downloading CSV:', error);
          this.$showErrorMessage('Failed to download CSV. Please try again.');
        } finally {
          this.isDownloadingCsv = false;
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .url {
    display: block;
  }
  .time-text {
    margin-bottom: 0;
  }
  .danger-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #f5222d;
    }
  }
  .warning-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #f1c40f;
    }
  }
  .edit-plan-type-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #5e9626;
    }
  }
  .policy-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #1890ff;
    }
  }
  .archive-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #595959;
    }
  }
  .webpage-tabs {
    padding: 0 24px;
    flex-shrink: 0;
    ::v-deep .ant-tabs-bar {
      margin-bottom: 0;
    }
  }
  .text-right {
    text-align: right;
  }
  .performance-chart {
    overflow-y: auto;
    padding: 0 0 20px;
    &::-webkit-scrollbar {
      height: 4px;
    }
  }
  .check-success {
    font-size: 40px;
    color: rgb(126, 207, 66);
  }
  .success-rebaseline-container {
    display: flex;
    align-items: center;
    .notice-text {
      font-size: 1rem;
    }
  }
  .success-bulk-edit-container {
    display: flex;
    align-items: center;
    .notice-text {
      font-size: 1rem;
    }
  }
  .check-warning {
    font-size: 40px;
    color: #faad14;
  }
  .bulk-edit-errors {
    border-top: 1px solid #f0f0f0;
    padding-top: 16px;
    .error-title {
      font-weight: 600;
      color: #595959;
    }
    .error-list {
      max-height: 200px;
      overflow-y: auto;
    }
    .error-item {
      padding: 8px 12px;
      background-color: #fff2f0;
      border: 1px solid #ffccc7;
      border-radius: 4px;
      margin-bottom: 8px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    .error-url {
      font-weight: 500;
      color: #262626;
      word-break: break-all;
      margin-bottom: 4px;
    }
    .error-message {
      color: #cf1322;
      font-size: 0.875rem;
    }
  }
  .text-muted {
    color: #8c8c8c;
    font-size: 0.875rem;
  }
</style>
